import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../context/FeedbackContext';
import ClientTagColorPicker from '../clients/ClientTagColorPicker';
import ClientTagChip from '../clients/ClientTagChip';
import {
  buildClientTagsSavePayload,
  createEmptyClientTag,
  validateClientTagsForm,
} from '../../utils/clientTags';

export default function ClientTagsSettingsPanel({
  form,
  setField,
  onSave,
  onReset,
  saving,
}) {
  const { t } = useTranslation();
  const { confirm, showError } = useFeedback();
  const tags = form.clientTags || [];

  const updateTags = (next) => setField('clientTags', next);

  const handleAdd = () => {
    updateTags([...tags, createEmptyClientTag()]);
  };

  const handleChange = (index, key, value) => {
    const next = tags.map((tag, i) => (i === index ? { ...tag, [key]: value } : tag));
    updateTags(next);
  };

  const handleDelete = async (index) => {
    const tag = tags[index];
    const ok = await confirm({
      title: t('settings.clientTags.deleteTitle'),
      message: t('settings.clientTags.deleteConfirm', { name: tag.name || t('common.unnamed') }),
      confirmLabel: t('common.delete'),
      confirmColor: 'error',
    });
    if (!ok) return;
    updateTags(tags.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const err = validateClientTagsForm(tags, t);
    if (err) {
      showError(err);
      return;
    }
    onSave(buildClientTagsSavePayload(tags));
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        {t('settings.clientTags.description')}
      </Typography>

      {tags.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {t('settings.clientTags.empty')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {tags.map((tag, index) => (
            <Stack
              key={tag._id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <TextField
                label={t('settings.clientTags.nameLabel')}
                value={tag.name}
                onChange={(e) => handleChange(index, 'name', e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 160 }}
                inputProps={{ maxLength: 40 }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('common.color')}
                </Typography>
                <ClientTagColorPicker
                  value={tag.color}
                  onChange={(color) => handleChange(index, 'color', color)}
                />
                {tag.name && (
                  <Box sx={{ mt: 1 }}>
                    <ClientTagChip tag={tag} />
                  </Box>
                )}
              </Box>
              <IconButton
                color="error"
                onClick={() => handleDelete(index)}
                aria-label={t('common.delete')}
                sx={{ alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }}>
        {t('settings.clientTags.addTag')}
      </Button>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
        <Button variant="outlined" onClick={onReset} disabled={saving}>{t('common.reset')}</Button>
      </Stack>
    </Stack>
  );
}
